export type AdminModule =
  | "licenses"
  | "products"
  | "customers"
  | "audit"
  | "monitor"
  | "staff"
  | "reports"
  | "apikeys"
  | "approvals"
  | "help";

export interface AdminModuleMeta {
  key: AdminModule;
  label: string;
  description: string;
  icon: string;
  path: string;
}

// 管理后台的模块清单：既用于路由鉴权，也用于管理员管理页的权限矩阵。
export const ADMIN_MODULES: AdminModuleMeta[] = [
  {
    key: "licenses",
    label: "许可证管理",
    description: "审批授权申请、签发 / 吊销 / 续期许可证",
    icon: "ri-key-2-line",
    path: "/admin",
  },
  {
    key: "products",
    label: "软件产品管理",
    description: "维护可授权软件目录、版本历史与授权类型，上架 / 下架",
    icon: "ri-apps-2-line",
    path: "/admin/products",
  },
  {
    key: "customers",
    label: "客户管理与跟进",
    description: "查看客户全貌、分配跟进负责人、批量指派风险客户",
    icon: "ri-group-line",
    path: "/admin/customers",
  },
  {
    key: "audit",
    label: "审计日志",
    description: "查看操作留痕、月度清理归档与批次设备回看",
    icon: "ri-file-history-line",
    path: "/admin/audit",
  },
  {
    key: "monitor",
    label: "授权状态监控",
    description: "激活量 / 请求量 / 可用率的运营看板",
    icon: "ri-line-chart-line",
    path: "/admin/monitor",
  },
  {
    key: "staff",
    label: "管理员管理",
    description: "授予 / 撤销管理员，调整角色权限矩阵",
    icon: "ri-shield-user-line",
    path: "/admin/staff",
  },
  {
    key: "reports",
    label: "报表中心",
    description: "统一收口各类 CSV 导出入口，并留存每一次导出任务历史",
    icon: "ri-file-chart-line",
    path: "/admin/reports",
  },
  {
    key: "apikeys",
    label: "对接方管理",
    description: "管理对接应用与 API Key、限流配额与校验接口调用日志",
    icon: "ri-plug-2-line",
    path: "/admin/apikeys",
  },
  {
    key: "approvals",
    label: "审批流与回调",
    description: "配置多级审批流、处理待审批申请，并管理事件回调（Webhook）订阅与投递",
    icon: "ri-flow-chart",
    path: "/admin/approvals",
  },
  {
    key: "help",
    label: "帮助中心反馈",
    description: "汇总常见问题的「有帮助 / 没帮助」反馈，定位最需要补充或改写的内容",
    icon: "ri-questionnaire-line",
    path: "/admin/help",
  },
];

export const ADMIN_ROLES = ["admin", "staff"] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ROLE_LABELS: Record<string, string> = {
  customer: "客户",
  staff: "客服",
  admin: "超管",
};

export const ASSIGNABLE_ROLES = ["customer", "staff", "admin"] as const;

// 默认权限矩阵：超管锁定为全量，客服默认可用除「管理员管理」之外的全部模块。
export const DEFAULT_ROLE_PERMISSIONS: Record<AdminRole, Record<AdminModule, boolean>> = {
  admin: {
    licenses: true,
    products: true,
    customers: true,
    audit: true,
    monitor: true,
    staff: true,
    reports: true,
    apikeys: true,
    approvals: true,
    help: true,
  },
  staff: {
    licenses: true,
    products: true,
    customers: true,
    audit: true,
    monitor: true,
    staff: false,
    reports: true,
    apikeys: false,
    approvals: false,
    help: true,
  },
};

export function isAdminRole(role: string | null | undefined): role is AdminRole {
  return role === "admin" || role === "staff";
}